import { startOfDay } from "date-fns";
import type { ChatApiMessage, ChatApiSession } from "./api";
import type {
  ChatHistoryGroup,
  ChatHistoryIcon,
  ChatContentParseResult,
  ChatMessage,
  ChatRole,
  ChatThread,
} from "./types";

export function formatChatTime(date: Date | string = new Date()) {
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) {
    return new Date().toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  }
  return d.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function titleFromPrompt(prompt: string) {
  const trimmed = prompt.trim();
  if (trimmed.length <= 32) return trimmed;
  return `${trimmed.slice(0, 32)}…`;
}

export function createEmptyThread(): ChatThread {
  return {
    id: `pending-${Date.now()}`,
    title: "New Chat",
    group: "today",
    icon: "message",
    messages: [],
  };
}

export function isPendingSessionId(id: string | null | undefined) {
  return !id || String(id).startsWith("pending-");
}

export function iconForTitle(title: string): ChatHistoryIcon {
  const t = title.toLowerCase();
  if (t.includes("record") || t.includes("folder")) return "folder";
  if (t.includes("note") || t.includes("summary")) return "file";
  if (t.includes("yesterday") || t.includes("history")) return "clock";
  return "message";
}

export function groupFromActivity(iso?: string | null): ChatHistoryGroup {
  if (!iso) return "today";
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "old";
  return at >= startOfDay(new Date()) ? "today" : "old";
}

function mapApiMessage(
  msg: ChatApiMessage,
  index: number,
  sessionId: string,
): ChatMessage | null {
  const role = String(msg.role || "").toLowerCase();
  if (role !== "user" && role !== "assistant") return null;
  const text = String(msg.content ?? "").trim();
  if (!text) return null;

  const parsed = parseChatContent(text, role as ChatRole);
  const at = msg.created_at || msg.timestamp;
  return {
    id: `${sessionId}-${role}-${index}-${at || index}`,
    role: role as ChatRole,
    type: parsed.type,
    text: parsed.text,
    time: formatChatTime(at || new Date()),
    intro: parsed.intro,
    attachment: parsed.attachment,
    table: parsed.table,
  };
}

/**
 * Returns a download payload when the reply contains a .csv URL; otherwise null
 * so the next extractor (or default `text`) can run.
 */
function extractDownloadAttachment(text: string): ChatContentParseResult | null {
  const match = text.match(/https?:\/\/[^\s)]+?\.csv[^\s)]+/i);
  if (!match) return null;

  const rawUrl = match[0].trim();
  if (!rawUrl) return null;

  const cleanedText = text
    .split("\n")
    .filter((line) => {
      const l = line.trim();
      if (!l) return true;
      const lower = l.toLowerCase();
      if (l.includes(rawUrl)) return false;
      if (lower.includes("this link expires")) return false;
      return true;
    })
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return {
    type: "download",
    text: cleanedText,
    attachment: {
      label: "Download sheet",
      href: rawUrl,
    },
  };
}

function splitMarkdownTableCells(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  return trimmed.split("|").map((cell) => cell.trim());
}

function isMarkdownTableSeparator(line: string): boolean {
  const cells = splitMarkdownTableCells(line);
  return (
    cells.length > 0 &&
    cells.every((cell) => /^:?-{3,}:?$/.test(cell.replace(/\s/g, "")))
  );
}

function isMarkdownTableRow(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed.includes("|")) return false;
  return splitMarkdownTableCells(trimmed).length >= 2;
}

/**
 * Detects a GitHub-flavored markdown table and keeps surrounding intro text.
 */
function extractMarkdownTable(text: string): ChatContentParseResult | null {
  const lines = text.split(/\r?\n/);
  let headerIndex = -1;
  for (let i = 0; i < lines.length - 1; i++) {
    if (isMarkdownTableRow(lines[i]) && isMarkdownTableSeparator(lines[i + 1])) {
      headerIndex = i;
      break;
    }
  }
  if (headerIndex < 0) return null;

  const headers = splitMarkdownTableCells(lines[headerIndex]);
  if (headers.length === 0) return null;

  const rows: string[][] = [];
  for (let i = headerIndex + 2; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) break;
    if (!isMarkdownTableRow(line)) break;
    const cells = splitMarkdownTableCells(line);
    if (cells.every((cell) => cell === "")) break;
    rows.push(
      headers.map((_, col) => cells[col] ?? ""),
    );
  }

  if (rows.length === 0) return null;

  const intro = lines.slice(0, headerIndex).join("\n").trim();
  return {
    type: "table",
    text,
    intro,
    table: { headers, rows },
  };
}

/** Add a new extractor here when a new assistant UI type is needed. */
const ASSISTANT_CONTENT_EXTRACTORS: Array<
  (text: string) => ChatContentParseResult | null
> = [extractDownloadAttachment, extractMarkdownTable];

export function parseChatContent(
  raw: string,
  role: ChatRole,
): ChatContentParseResult {
  if (role === "user") {
    return { type: "user", text: raw };
  }
  for (const extract of ASSISTANT_CONTENT_EXTRACTORS) {
    const parsed = extract(raw);
    if (parsed) return parsed;
  }
  return { type: "text", text: raw };
}

export function mapApiSessionToThread(session: ChatApiSession): ChatThread {
  const sessionId = String(session.session_id || "").trim();
  const messages = (session.messages || [])
    .map((m, i) => mapApiMessage(m, i, sessionId))
    .filter(Boolean) as ChatMessage[];

  const firstUser = messages.find((m) => m.role === "user");
  const title = firstUser
    ? titleFromPrompt(firstUser.text)
    : `Chat ${sessionId.slice(0, 8)}`;

  return {
    id: sessionId,
    title,
    group: groupFromActivity(session.last_activity_at),
    icon: iconForTitle(title),
    messages,
  };
}

export function mapApiSessionsToThreads(
  sessions: ChatApiSession[] | undefined,
): ChatThread[] {
  if (!Array.isArray(sessions)) return [];
  return sessions
    .filter((s) => s?.session_id)
    .map(mapApiSessionToThread);
}

export function mapSessionListToThreads(
  sessions: Array<{
    session_id: string;
    created_at?: string;
    last_activity_at?: string;
    title?: string;
  }>,
): ChatThread[] {
  return sessions
    .filter((s) => s?.session_id)
    .map((session) => {
      const sessionId = String(session.session_id).trim();
      const title =
        String(session.title || "").trim() ||
        `Chat ${sessionId.slice(0, 8)}`;
      return {
        id: sessionId,
        title,
        group: groupFromActivity(session.last_activity_at || session.created_at),
        icon: iconForTitle(title),
        messages: [],
      };
    });
}

export function mergeSessionMessages(
  thread: ChatThread,
  messages: ChatApiMessage[] | undefined,
): ChatThread {
  const sessionId = thread.id;
  const mapped = (messages || [])
    .map((m, i) => mapApiMessage(m, i, sessionId))
    .filter(Boolean) as ChatMessage[];
  return {
    ...thread,
    messages: mapped.length > 0 ? mapped : thread.messages,
  };
}

export function resolveAllowedOrgIds(
  selectedOrgId: string | null,
  isAllSelected: boolean,
  organizations: Array<{ _id?: string }>,
): string {
  if (isAllSelected || !selectedOrgId || selectedOrgId === "anthro-care") {
    return organizations
      .map((o) => String(o?._id || "").trim())
      .filter(Boolean)
      .join(", ");
  }
  return String(selectedOrgId).trim();
}
